<script setup lang="ts">
import Extension from "@/core/interfaces/extension/Extension";
import ExtensionLogo from "@/extension/components/extension-logo/ExtensionLogo.vue";
import ToggleSwitch from "@/program/components/support-program/SupportProgramNotificationToggle/ToggleSwitch.vue";
import {computed, ref} from "vue";
import ExtensionService from "@/extension/services/ExtensionService";
import StripePriceService from "@/stripe/services/stripePriceService";
import StripeCheckoutService from "@/stripe/services/stripeCheckoutService";
import {useToastStore} from "@/stores/toast.store";
import {useRouter} from "vue-router";
import NewFeatureTag from "@/core/components/tags/NewFeatureTag.vue";
import {useSubscriptionPlanStore} from "@/stores/subscription-plan.store";
import {useExtensionStore} from "@/stores/extension.store";

const {enableToggle = false, extension} = defineProps<{
    extension: Extension,
    enableToggle?: boolean;
}>();

const router = useRouter();
const toast = useToastStore();
const {globalPlan} = useSubscriptionPlanStore();
const extensionStore = useExtensionStore();

const loading = ref(false);

/**
 * The toggle state, reflecting the extension activation held by the store.
 */
const on = computed<boolean | undefined>({
    get() {
        return extension.enabled !== null ? extension.enabled : undefined;
    },
    set() {
        toggle();
    }
});

/**
 * Toggle the activation of the extension.
 */
const toggle = () => {
    if (enableToggle && !loading.value) {
        loading.value = true;
        return toggleActivation()?.finally(() => {
            loading.value = false;
        });
    }
};


/**
 * Buy the extension.
 */
const buyExtension = async (extension: Extension) => {
    const price = StripePriceService.featuredPrice(extension.prices);

    if (price) {
        return StripeCheckoutService.checkout(price);
    }
};


/**
 * Toggle the activation of the extension.
 */
const toggleActivation = () => {
    const nextAction = ExtensionService.getNextAction(extension, globalPlan);

    if (nextAction.action === 'disable') {
        return extensionStore.disableExtension(extension.id)
            .then(() => {
                toast.showInfo({
                    content: "Module désactivé."
                });
            });
    } else if (nextAction.action === 'enable') {
        return extensionStore.enableExtension(extension.id)
            .then(() => {
                toast.showSuccess({
                    content: "Module activé ✅ Les patients ciblés seront alertés au comptoir sur tous vos postes Connect."
                });
            });
    } else if (nextAction.action === 'buy') {
        if (extension.subscription_plan) {
            router.push({
                name: 'subscription-plans'
            });
        } else {
            buyExtension(extension);
        }
    }
};
</script>

<template>
  <div>
    <div
      class="relative flex flex-col"
      :class="[{'pb-3': !extension.is_new && enableToggle}]"
    >
      <ExtensionLogo :extension="extension" class="z-10 hover:border-transparent hover:border-2" />
      <NewFeatureTag
        v-if="extension.is_new"
        class="self-end rounded-b-md px-2 relative -top-1 z-11 rounded-t-none"
        :class="[{'pt-5 top-.5 pb-1': enableToggle && extension.is_new}, {'pt-3.5 pb-2.5': !(enableToggle && extension.is_new)}]"
      />
      <div
        v-if="enableToggle"
        class="absolute z-10 bg-white h-6 w-12 rounded-xl"
        :class="[{'bottom-0 left-8': !extension.is_new}, {'bottom-8 left-8': extension.is_new}]"
      />
      <div
        v-if="enableToggle"
        class="absolute z-10"
        :class="[{'-bottom-3 left-9': !extension.is_new}, {'bottom-5 left-9': extension.is_new}]"
      >
        <ToggleSwitch
          v-model="on"
          color="#C2F5E4"
          thumb-color="#65efca"
          size="small"
          :is-loading="loading"
          @click.stop=""
        />
      </div>
    </div>
    <p
      v-if="!extension.is_new"
      class="text-gray-900 text-sm text-start break-words"
      :class="{
        'line-clamp-1': extension.is_new,
        'line-clamp-2': !extension.is_new,
        'mt-1': !extension.is_new,
      }"
      style="max-width: 100px;"
    >
      {{
        extension.trial_vouchers?.length && extension.latest_active_trial
          ? extension.trial_vouchers[0].description ?? extension.name
          : extension.name
      }}
    </p>
  </div>
</template>

<style scoped>

</style>
